import React, { useEffect, useState } from 'react';
import axios from 'axios';
import serverURL from '../config';
import { useNavigate } from 'react-router-dom';
import EventModal from '../components/EventModal';
import 'react-tooltip/dist/react-tooltip.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faUsers, faPlus } from '@fortawesome/free-solid-svg-icons';
import CardActions from '../components/CardActions';
import { useAuth } from '../context/AuthContext';
import { FiFilter } from 'react-icons/fi';

const EVENT_FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Expired', value: 'expired' },
];

const EventList = () => {
  const [events, setEvents] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [eventTitle, setEventTitle] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const navigate = useNavigate();
  const { role } = useAuth();
  const canManageEvents = role === 'admin';

  const fetchEvents = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${serverURL}/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const closeEventModal = () => {
    setModalOpen(false);
    setSelectedEvent(null);
  };

  const handleAdd = () => {
    if (!canManageEvents) return;
    setModalOpen(true);
    setEventTitle('Add Event');
  };

  const handleEdit = (event) => {
    if (!canManageEvents) return;
    setSelectedEvent(event);
    setModalOpen(true);
    setEventTitle('Edit Event');
  };

  const handleDelete = (event) => {
    if (!canManageEvents) return;
    setSelectedEvent(event);
    setModalOpen(true);
    setEventTitle('Delete Event');
  };

  // Filter events based on selected filter
  const filteredEvents = events.filter(event => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'active') return event.status !== 'completed';
    if (filterStatus === 'expired') return event.status === 'completed';
    return true;
  });

  return (
    <div className="flex flex-col min-h-screen bg-campus-bg">
      <main className="flex-grow p-6">
        <div className="flex justify-between mb-5">
          <h1 className="text-2xl font-heading text-campus-text-primary">Events List</h1>
          <div className='flex gap-3 items-center'>
            {/* Animated Segmented Filter Control */}
            <div
              className="hover-expand-container bg-campus-surface border border-campus-border rounded-lg p-1 shadow-card z-20 max-w-full"
              style={{
                '--expand-width': '18rem',
                '--content-max-width': '16rem',
                '--content-margin': '0.5rem'
              }}
            >
              <div className="hover-expand-icon text-campus-text-body px-1 cursor-pointer">
                <FiFilter size={18} />
              </div>
              <div className="hover-expand-content flex gap-1">
                {EVENT_FILTERS.map((f) => (
                  <button
                    key={f.value}
                    onClick={() => setFilterStatus(f.value)}
                    className={`relative px-4 py-1.5 text-sm font-medium transition-all duration-300 rounded-md whitespace-nowrap ${filterStatus === f.value ? 'bg-campus-primary text-white shadow-sm' : 'bg-transparent text-campus-text-body hover:bg-campus-bg hover:text-campus-text-primary'}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Animated Add Event Button */}
            {canManageEvents && (
              <button
                onClick={handleAdd}
                className="hover-expand-container bg-campus-primary text-white rounded-lg font-medium cursor-pointer h-10 px-3 hover:bg-campus-primary-dark shadow-sm hover:shadow-lg focus-visible:outline-none"
                style={{
                  '--expand-width': '8.5rem',
                  '--expand-padding': '0 1rem'
                }}
                aria-label="Add Event"
              >
                <span className="hover-expand-icon">
                  <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                </span>
                <span className="hover-expand-content text-sm" aria-hidden="true">
                  Add Event
                </span>
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEvents.length === 0 && (
            <p className="text-campus-text-body">No events found.</p>
          )}
          {filteredEvents.map((event) => (
            <div
              key={event.id}
              className="bg-campus-surface p-4 rounded-card shadow-card border border-campus-border card-hover-effect"
            >
              <div className='flex justify-end gap-3'>
                <div className={`flex items-center text-white text-xs font-bold py-1 px-2 rounded-full ${event.status === 'completed' ? 'bg-campus-error' : 'bg-campus-success'
                  }`}>
                  {event.status === 'completed' ? 'EXPIRED' : 'ACTIVE'}
                </div>
              </div>
              <h3 className="text-lg font-semibold mt-3 text-campus-text-primary">{event.name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-campus-text-body">Institution: {event.institution}</p>
                <div className="flex items-center bg-campus-bg text-campus-text-primary text-xs font-bold py-1 px-2 rounded-full border border-campus-border">
                  <FontAwesomeIcon icon={faUsers} className="mr-1" />
                  {event.candidate_count} Candidates
                </div>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-campus-text-body">Date: {new Date(event.event_date).toLocaleDateString()}</p>
                <div className="flex items-center bg-campus-bg text-campus-text-primary text-xs font-bold py-1 px-2 rounded-full border border-campus-border">
                  <FontAwesomeIcon icon={faClock} className="mr-1" />
                  {event.duration} min
                </div>
              </div>
              <CardActions
                onViewDetails={() =>
                  navigate(
                    canManageEvents
                      ? `/event/${event.id}/${encodeURIComponent(event.name)}`
                      : `/consolidated/${event.id}`
                  )
                }
                onEdit={() => handleEdit(event)}
                onDelete={() => handleDelete(event)}
                showManageActions={canManageEvents}
              />
            </div>
          ))}
        </div>
      </main>

      {/* Modal */}
      {modalOpen && (
        <EventModal
          title={eventTitle}
          event={selectedEvent}
          closeEventModal={closeEventModal}
          fetchEvents={fetchEvents}
        />
      )}
    </div>
  );
};

export default EventList;